import React from 'react';
import { motion } from 'motion/react';
import { ROADMAP } from '../constants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, Map, Compass } from 'lucide-react';

const STATUS_STYLES: Record<string, { color: string; icon: any; label: string }> = {
  COMPLETE: { color: 'bg-emerald-700 text-emerald-100 border-emerald-600', icon: CheckCircle2, label: 'Verified Complete' },
  IN_PROGRESS: { color: 'bg-amber-800/60 text-amber-200 border-amber-600/40', icon: Clock, label: 'In Progress' },
  PLANNED: { color: 'bg-blue-900/60 text-blue-200 border-blue-600/40', icon: Map, label: 'Planned' },
  VISION: { color: 'bg-slate-800 text-slate-300 border-slate-600', icon: Compass, label: 'Long-Term Vision' },
};

export default function Roadmap() {
  return (
    <section id="roadmap" className="py-24 px-6 bg-slate-900 text-white overflow-hidden border-t border-slate-800">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <Badge className="mb-4 bg-emerald-500 text-white hover:bg-emerald-600">The Journey Ahead</Badge>
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4">Roadmap to 2030</h2>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl">
            We aren't just building an app; we're building the infrastructure for a nation's human development.
            Phase 01 is verified complete as of September 2026.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {ROADMAP.map((phase: any, index: number) => {
            const statusKey = (phase.status || 'PLANNED') as string;
            const style = STATUS_STYLES[statusKey] || STATUS_STYLES['PLANNED'];
            const StatusIcon = style.icon;
            return (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative z-10"
              >
                <Card className={`bg-slate-800/50 border-slate-700 text-white h-full ${statusKey === 'COMPLETE' ? 'border-emerald-600/40 ring-1 ring-emerald-600/20' : ''}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-emerald-400 font-mono text-xs uppercase tracking-widest">{phase.phase}</span>
                      <span className="text-slate-500 text-xs font-mono">{phase.period}</span>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold font-mono border mb-2 w-fit ${style.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {style.label}
                    </div>
                    <CardTitle className="text-xl font-bold text-white">{phase.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {phase.items.map((item: string, i: number) => (
                        <li key={i} className={`text-xs flex gap-2 ${statusKey === 'COMPLETE' ? 'text-emerald-200' : 'text-slate-300'}`}>
                          <span className={statusKey === 'COMPLETE' ? 'text-emerald-500' : 'text-emerald-500'}>•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
